import { useState, useRef, useCallback, type ReactNode, type TouchEvent } from "react";

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
}

export default function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [state, setState] = useState<"idle" | "pulling" | "releasing" | "refreshing">("idle");
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const pullingRef = useRef(false);
  const threshold = 56;

  const handleTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    if (state === "refreshing") return;
    pullingRef.current = true;
    startY.current = e.touches[0].clientY;
  }, [state]);

  const handleTouchMove = useCallback((e: TouchEvent<HTMLDivElement>) => {
    if (!pullingRef.current || state === "refreshing") return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta < 0) {
      setPullDistance(0);
      setState("idle");
      return;
    }
    const damped = Math.min(delta * 0.45, 110);
    setPullDistance(damped);
    setState(damped >= threshold ? "releasing" : "pulling");
  }, [state, threshold]);

  const handleTouchEnd = useCallback(async () => {
    pullingRef.current = false;
    if (state === "releasing") {
      setState("refreshing");
      setPullDistance(threshold);
      try {
        await onRefresh();
      } finally {
        setState("idle");
        setPullDistance(0);
      }
    } else {
      setState("idle");
      setPullDistance(0);
    }
  }, [state, onRefresh, threshold]);

  const indicatorHeight = state === "refreshing" ? threshold : pullDistance;

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200 ease-out"
        style={{ height: `${indicatorHeight}px` }}
      >
        <div className="flex items-center gap-2">
          {state === "refreshing" ? (
            <>
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-primary-500 font-medium">刷新中...</span>
            </>
          ) : (
            <>
              <div className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-primary-500 transition-transform duration-200 ${
                    state === "releasing" ? "rotate-180" : ""
                  }`}
                  style={{ fontSize: "18px" }}
                />
              </div>
              <span className="text-xs text-foreground-400">
                {state === "releasing" ? "松开刷新" : "下拉刷新"}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Content slides down when pulling */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: state === "idle" ? "translateY(0)" : `translateY(${indicatorHeight}px)`,
        }}
      >
        {children}
      </div>
    </div>
  );
}